import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Images, Library, Shield, CreditCard, Link2, Unlink } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { UserAvatar } from '../components/AccountMenu';

type AccountPayload = {
  profile: {
    displayName: string | null;
    email: string | null;
    photoURL: string | null;
    provider: string;
  };
  entitlement: {
    credits: number;
    freeRestorationsRemaining: number;
    canRestore: boolean;
    stage?: string;
    creditMode?: string;
    creditsEnforced?: boolean;
    restorationsCurrentlyFree?: boolean;
    billingEnabled?: boolean;
    planLabel?: string;
  };
  billing: {
    billingEnabled: boolean;
    hasStripeCustomer: boolean;
    planLabel: string;
    subscription: null;
  };
  library: { recentJobCount: number };
};

type HistoryEntry = {
  id: string;
  type: string;
  label: string;
  amount: number;
  createdAt: string | null;
};

function providerLabel(provider: string) {
  if (provider === 'google.com') return 'Google';
  if (provider === 'password') return 'Email & password';
  if (provider === 'dev') return 'Development';
  return provider || 'Sign-in provider';
}

function formatDate(iso: string | null) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export const AccountPage: React.FC = () => {
  const { token, user, productConfig, balance, refreshBalance } = useAuth();
  const [account, setAccount] = useState<AccountPayload | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [photos, setPhotos] = useState<{
    connected: boolean;
    configured?: boolean;
    message?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [acct, hist, status] = await Promise.all([
        api.account(token),
        api.creditHistory(token).catch(() => ({ entries: [] as HistoryEntry[] })),
        api.googlePhotosStatus(token).catch(() => ({ connected: false })),
      ]);
      setAccount(acct as AccountPayload);
      setHistory((hist.entries || []) as HistoryEntry[]);
      setPhotos(status);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load account');
    }
  }, [token]);

  useEffect(() => {
    load();
    refreshBalance();
  }, [load, refreshBalance]);

  useEffect(() => {
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [account]);

  const stage = account?.entitlement.stage || productConfig?.stage || 'alpha';
  const creditMode =
    account?.entitlement.creditMode || productConfig?.creditMode || balance?.creditMode || 'disabled';
  const billingEnabled = Boolean(
    account?.billing.billingEnabled ?? productConfig?.billingEnabled
  );
  const displayName = user?.displayName || account?.profile.displayName || 'Your account';
  const email = user?.email || account?.profile.email || '';

  const connectPhotos = async () => {
    setBusy('photos');
    setMessage(null);
    try {
      const { url } = await api.googlePhotosAuthUrl(token);
      window.location.href = url;
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not start Google Photos connection');
      setBusy(null);
    }
  };

  const disconnectPhotos = async () => {
    setBusy('photos-disconnect');
    try {
      await api.googlePhotosDisconnect(token);
      setPhotos({ connected: false, configured: photos?.configured });
      setMessage('Google Photos disconnected.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not disconnect Google Photos');
    } finally {
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy('portal');
    setMessage(null);
    try {
      const session = await api.billingPortal(token);
      if (session.url) window.location.href = session.url;
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not open billing portal');
      setBusy(null);
    }
  };

  const buyPack = async (packId: string) => {
    setBusy(packId);
    setMessage(null);
    try {
      const session = await api.checkout(packId, token);
      if (session.mock) {
        setMessage('Test checkout completed in this environment.');
        await refreshBalance();
        await load();
      } else if (session.url) {
        window.location.href = session.url;
        return;
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Checkout failed');
    } finally {
      setBusy(null);
    }
  };

  const [packs, setPacks] = useState<
    Array<{ id: string; credits: number; label: string; suggestedUsd: number; purchasable?: boolean }>
  >([]);

  useEffect(() => {
    if (!billingEnabled) return;
    api
      .packs()
      .then((r) => setPacks(r.packs || []))
      .catch(() => setPacks([]));
  }, [billingEnabled]);

  return (
    <main className="kin-page kin-account-page">
      <div className="kin-page-head">
        <h1>Account</h1>
        <Link to="/app" className="kin-btn kin-btn-ghost">
          Back to Studio
        </Link>
      </div>

      {error && <p className="kin-banner kin-banner-error">{error}</p>}
      {message && <p className="kin-banner">{message}</p>}

      <section className="kin-account-card" aria-labelledby="account-profile">
        <div className="kin-account-profile">
          <UserAvatar size={64} />
          <div>
            <h2 id="account-profile">{displayName}</h2>
            {email ? <p className="kin-muted">{email}</p> : null}
            <p className="kin-account-meta">
              Signed in with {providerLabel(account?.profile.provider || 'google.com')}
            </p>
            <p className="kin-footnote">
              Profile information comes from your sign-in provider.
            </p>
          </div>
        </div>
      </section>

      <section className="kin-account-card" aria-labelledby="account-status">
        <h2 id="account-status">Kinvera status</h2>
        <div className="kin-account-status-row">
          <span className="kin-credits kin-access-badge">
            {stage === 'beta' ? 'Kinvera Beta' : stage === 'production' ? 'Kinvera' : 'Kinvera Alpha'}
          </span>
          <span className="kin-account-plan">
            Current plan: <strong>{account?.billing.planLabel || 'Pay as you go'}</strong>
          </span>
        </div>
        {creditMode === 'disabled' && (
          <p>Restorations are currently free while we test and improve Kinvera.</p>
        )}
        {creditMode === 'metered' && (
          <p>
            Kinvera is tracking restoration usage, but you are not currently being charged.
          </p>
        )}
        {creditMode === 'enforced' && (
          <p>
            Available credits:{' '}
            <strong>
              {account?.entitlement.credits ?? balance?.credits ?? 0}
            </strong>
            {(account?.entitlement.freeRestorationsRemaining ||
              balance?.freeRestorationsRemaining) ? (
              <>
                {' '}
                ·{' '}
                {account?.entitlement.freeRestorationsRemaining ||
                  balance?.freeRestorationsRemaining}{' '}
                promotional free
              </>
            ) : null}
          </p>
        )}
        <p className="kin-footnote">No active subscription.</p>
      </section>

      <section className="kin-account-card" id="billing" aria-labelledby="account-billing">
        <h2 id="account-billing">
          <CreditCard className="kin-account-section-icon" aria-hidden /> Credits &amp; billing
        </h2>

        {!billingEnabled && creditMode === 'disabled' && (
          <p>
            <strong>Alpha access</strong> — no credits are required right now. Billing is not active
            during Alpha. No payment method is required.
          </p>
        )}
        {!billingEnabled && creditMode === 'metered' && (
          <p>
            <strong>Beta access</strong> — Kinvera is tracking restoration usage, but you are not
            currently being charged. Billing remains disabled.
          </p>
        )}
        {billingEnabled && (
          <>
            <p>
              Available credits:{' '}
              <strong>{account?.entitlement.credits ?? balance?.credits ?? 0}</strong>
            </p>
            <div className="kin-pricing-grid kin-account-packs">
              {packs.map((pack) => (
                <article key={pack.id} className="kin-price-card">
                  <h3>{pack.label}</h3>
                  <p className="kin-price">
                    ${Number(pack.suggestedUsd).toFixed(2)}
                    <span> · {pack.credits} restorations</span>
                  </p>
                  <button
                    type="button"
                    className="kin-btn kin-btn-primary"
                    disabled={busy === pack.id || pack.purchasable === false}
                    onClick={() => buyPack(pack.id)}
                  >
                    {busy === pack.id ? 'Working…' : 'Buy credits'}
                  </button>
                </article>
              ))}
            </div>
            {account?.billing.hasStripeCustomer && (
              <button
                type="button"
                className="kin-btn kin-btn-ghost"
                disabled={busy === 'portal'}
                onClick={openPortal}
              >
                {busy === 'portal' ? 'Opening…' : 'Manage billing'}
              </button>
            )}
          </>
        )}

        {!billingEnabled && (
          <p className="kin-footnote">
            When billing is enabled, you will be able to buy restoration credit packs here. See{' '}
            <Link to="/pricing">Pricing</Link> for the product model.
          </p>
        )}

        {history.length > 0 && (
          <div className="kin-account-history">
            <h3>Credit activity</h3>
            <ul>
              {history.map((entry) => (
                <li key={entry.id}>
                  <div>
                    <strong>{formatDate(entry.createdAt)}</strong>
                    <span>{entry.label}</span>
                  </div>
                  <span
                    className={
                      entry.amount > 0
                        ? 'kin-account-amount-pos'
                        : entry.amount < 0
                          ? 'kin-account-amount-neg'
                          : 'kin-muted'
                    }
                  >
                    {entry.amount > 0 ? '+' : ''}
                    {entry.amount}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="kin-account-card" id="connected" aria-labelledby="account-connected">
        <h2 id="account-connected">
          <Images className="kin-account-section-icon" aria-hidden /> Connected services
        </h2>
        <div className="kin-account-service">
          <div>
            <strong>Google Photos</strong>
            <p className="kin-muted">
              {photos?.connected
                ? 'Connected — import photos and save restorations to your library.'
                : photos?.configured === false
                  ? 'Google Photos is not configured in this environment.'
                  : 'Not connected'}
            </p>
          </div>
          <div className="kin-account-service-actions">
            {photos?.connected ? (
              <button
                type="button"
                className="kin-btn kin-btn-ghost"
                disabled={busy === 'photos-disconnect'}
                onClick={disconnectPhotos}
              >
                <Unlink size={16} /> Disconnect
              </button>
            ) : (
              <button
                type="button"
                className="kin-btn kin-btn-primary"
                disabled={busy === 'photos' || photos?.configured === false}
                onClick={connectPhotos}
              >
                <Link2 size={16} /> Connect
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="kin-account-card" aria-labelledby="account-library">
        <h2 id="account-library">
          <Library className="kin-account-section-icon" aria-hidden /> Your photos and data
        </h2>
        <p>
          {typeof account?.library.recentJobCount === 'number'
            ? `${account.library.recentJobCount} recent restoration${
                account.library.recentJobCount === 1 ? '' : 's'
              } in your Library.`
            : 'Open your Library to review recent restorations.'}
        </p>
        <p className="kin-footnote">
          During Alpha, automatic long-term deletion is not fully enforced. You can delete individual
          jobs from Library anytime.
        </p>
        <Link to="/app/library" className="kin-btn kin-btn-ghost">
          Open Library
        </Link>
      </section>

      <section className="kin-account-card" aria-labelledby="account-privacy">
        <h2 id="account-privacy">
          <Shield className="kin-account-section-icon" aria-hidden /> Privacy
        </h2>
        <p>
          Read how Kinvera handles photographs and account data in our{' '}
          <Link to="/privacy">Privacy</Link> and <Link to="/terms">Terms</Link> pages.
        </p>
        <p className="kin-footnote">
          Full account deletion (Firebase auth, stored photos, OAuth tokens, and billing
          relationship) is deferred until cleanup can be completed safely end-to-end.
        </p>
      </section>
    </main>
  );
};
